import { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { CustomModal } from 'components/elements';
import { convertCoinsToUAH, convertUAHToCoins, getLocaleAmountString } from 'utils/helpers';

export interface IPaymentRefundRow {
	clientId: number;
	paymentId: string;
	provider: string;
	amountRaw: number;
	feeRaw: number;
	billParentFeeRaw?: null | number;
}

interface IPaymentRefundValues {
	amount?: number;
	withoutBank: 'YES' | 'NO';
}

interface PaymentRefundModalProps {
	open: boolean;
	payment: IPaymentRefundRow | null;
	isSubmitting: boolean;
	onClose: () => void;
	onSubmit: (values: IPaymentRefundValues) => Promise<void>;
}

const formatAmount = (amount?: null | number): string => {
	if (amount === null || amount === undefined) return '-';

	return getLocaleAmountString(convertCoinsToUAH(amount));
};

export const PaymentRefundModal: FC<PaymentRefundModalProps> = ({ open, payment, isSubmitting, onClose, onSubmit }) => {
	const [isFullAmount, setIsFullAmount] = useState(true);
	const [isNotifyBank, setIsNotifyBank] = useState(true);
	const [refundAmount, setRefundAmount] = useState('');
	const [isRefundAmountTouched, setIsRefundAmountTouched] = useState(false);

	useEffect(() => {
		if (open) {
			setIsFullAmount(true);
			setIsNotifyBank(true);
			setRefundAmount('');
			setIsRefundAmountTouched(false);
		}
	}, [open, payment?.paymentId]);

	const refundAmountNumber = useMemo(() => Number(refundAmount), [refundAmount]);
	const maxRefundAmount = useMemo(() => (payment ? convertCoinsToUAH(payment.amountRaw) : 0), [payment]);
	const isRefundAmountInvalid = !isFullAmount && (!refundAmountNumber || refundAmountNumber <= 0 || refundAmountNumber > maxRefundAmount);
	const showRefundAmountError = isRefundAmountTouched && isRefundAmountInvalid;
	const refundAmountHelperText =
		refundAmountNumber > maxRefundAmount
			? 'Сума повернення перевищує загальну'
			: 'Вкажіть суму повернення';

	const handleFullAmountChange = (checked: boolean) => {
		setIsFullAmount(checked);
		setIsRefundAmountTouched(false);
	};

	const handleSubmit = async () => {
		if (!payment) return;

		if (isRefundAmountInvalid) {
			setIsRefundAmountTouched(true);
			return;
		}

		await onSubmit({
			withoutBank: isNotifyBank ? 'YES' : 'NO',
			...(isFullAmount ? {} : { amount: convertUAHToCoins(refundAmountNumber) }),
		});
	};

	return (
		<CustomModal open={open} onClose={onClose} title="Повернення оплати" maxWidth="md" fullWidth>
			<Stack spacing={2}>
				<Box>
					<Typography variant="body2" color="text.primary">
						Банк-еквайр
					</Typography>
					<Typography variant="body1">{payment?.provider || '-'}</Typography>
				</Box>

				<Stack spacing={1}>
					<Box display="flex" justifyContent="space-between" gap={2}>
						<Typography color="text.primary">Загальна сума</Typography>
						<Typography>{formatAmount(payment?.amountRaw)}</Typography>
					</Box>
					<Box display="flex" justifyContent="space-between" gap={2}>
						<Typography color="text.primary">Комісія основного рахунку</Typography>
						<Typography>{formatAmount(payment?.billParentFeeRaw)}</Typography>
					</Box>
					<Box display="flex" justifyContent="space-between" gap={2}>
						<Typography color="text.primary">Комісія</Typography>
						<Typography>{formatAmount(payment?.feeRaw)}</Typography>
					</Box>
				</Stack>

				<FormControlLabel
					control={
						<Checkbox checked={isFullAmount} onChange={(event) => handleFullAmountChange(event.target.checked)} />
					}
					label="Повернути повну суму"
				/>

				<TextField
					label="Сума повернення"
					type="number"
					value={refundAmount}
					disabled={isFullAmount}
					error={showRefundAmountError}
					helperText={showRefundAmountError ? refundAmountHelperText : ' '}
					FormHelperTextProps={{
						sx: {
							color: 'text.secondary',
						},
					}}
					onBlur={() => setIsRefundAmountTouched(true)}
					onChange={(event) => {
						setRefundAmount(event.target.value);
						setIsRefundAmountTouched(true);
					}}
					inputProps={{ min: 0, max: maxRefundAmount, step: 0.01 }}
					fullWidth
				/>

				<FormControlLabel
					control={<Checkbox checked={isNotifyBank} onChange={(event) => setIsNotifyBank(event.target.checked)} />}
					label="Сповістити банк-еквайр"
				/>

				<Box display="flex" justifyContent="flex-end" gap={1.5}>
					<Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
						Скасувати
					</Button>
					<Button
						variant="contained"
						onClick={handleSubmit}
						disabled={isSubmitting || !payment || isRefundAmountInvalid}
					>
						Зробити повернення
					</Button>
				</Box>
			</Stack>
		</CustomModal>
	);
};
